import React, { useMemo } from "react";
import "./HomePage.css";
import { useSearchParams } from "react-router-dom";
import { useCompanyDetails } from "../hooks/useCompanyDetails";
import { useMarketCardOnHomePage } from "../hooks/useMarketCardOnHomePage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import WHEELBARROW from "../assets/images/graphics/wheelbarrow.avif";
import ARROSOIR from "../assets/images/graphics/arrosoir.avif";
import TRUELLE from "../assets/images/graphics/truelle.avif";
import { getOrderedInfoCards } from "../utils/homePage.helpers";
import { resolvePublicNavDate } from "../config/publicNavLinks";
import HomeFlipCard from "../components/HomeFlipCard";
import MarketHomeCardBody from "../components/MarketHomeCardBody";
import Hero from "../components/Hero";
import CTAHero from "../components/CTAHero";
import { formatPhoneNumber } from "../components/dashboard/utils/PhoneFormatters";
import LoadingSpinner from "../components/LoadingSpinner";

type SocialPlatform = "instagram" | "facebook" | "linkedin";

function HomeSocialLink({
  href,
  platform,
  label,
}: {
  href?: string;
  platform: SocialPlatform;
  label: string;
}) {
  const hasLink = Boolean(href?.trim());

  return (
    <a
      href={hasLink ? href : ""}
      className={hasLink ? undefined : "home-social-link-pending"}
      target={hasLink ? "_blank" : undefined}
      rel={hasLink ? "noopener noreferrer" : undefined}
      aria-label={label}
      aria-disabled={!hasLink}
      tabIndex={hasLink ? 0 : -1}
      onClick={(e) => {
        if (!hasLink) e.preventDefault();
      }}
      onKeyDown={(e) => {
        if (!hasLink && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
        }
      }}
    >
      <FontAwesomeIcon icon={["fab", platform]} className="home-icon" />
    </a>
  );
}

const HomePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { data, loading } = useCompanyDetails();
  const { data: marketCard, loading: marketCardLoading } =
    useMarketCardOnHomePage();
  const showMarketCard = Boolean(marketCard?.title) && !marketCardLoading;
  const supportLink = data?.supportUsLink;
  const orderedCards = useMemo(
    () => getOrderedInfoCards(resolvePublicNavDate()),
    [searchParams],
  );
  const openSupportLink = () => {
    if (!supportLink) return;
    window.open(supportLink, "_blank", "noopener,noreferrer");
  };
  const cards = orderedCards.map((card, index) => {
    return (
      <div
        key={index + card.altText}
        className={`home-grid-item item${index + 1}`}
      >
        <HomeFlipCard
          image={card.image}
          altText={card.altText}
          imageTitle={card.imageTitle}
          body={card.body.body}
          buttonText={card.body.buttonText}
          link={card.body.link}
        />
      </div>
    );
  });

  return (
    <>
      <Hero
        page="home"
        titleOne={<>Alnarp's Agroecology Farm</>}
        titleTwo="Growing Together"
        contrastColor={true}
      />

      <CTAHero
        background="green"
        title="Learning and exploring agroecology through doing"
        textColor="darkgreen"
        xl={false}
      />
      <br />

      <section className="content-container home-container">
        <div
          className={
            showMarketCard ? "home-grid home-grid--has-market" : "home-grid"
          }
        >
          <div className="home-grid-item item11">
            <CTAHero
              background="image"
              textColor="green"
              link="/csa"
              linkText="Get your veggie box!"
              xl={false}
            />
          </div>
          {cards.slice(0, 3)}
          <div className="home-grid-item item12">
            <div
              className="help-us-grow info-card info-card-clickable green"
              role="link"
              tabIndex={0}
              onClick={openSupportLink}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openSupportLink();
                }
              }}
              aria-label="Open support page in a new tab"
            >
              <div className="crow-funder-img-container">
                <img src={TRUELLE} alt="Alnarp farm tool graphics" />
                <img src={WHEELBARROW} alt="Alnarp farm tool graphics" />
                <img src={ARROSOIR} alt="Alnarp farm tool graphics" />
              </div>
              <h2>AS A SMALL FARM YOUR GENEROSITY HELPS US GROW</h2>
            </div>
          </div>
          {showMarketCard && marketCard && (
            <div className="home-grid-item item-market">
              <div className="home-grid-container info-card yellow">
                <div className="market-container">
                  <h2 className="market-card-header">{marketCard.title}</h2>
                  <MarketHomeCardBody
                    marketInformation={marketCard.marketInformation}
                  />
                </div>
              </div>
            </div>
          )}
          {cards.slice(3)}
          <div className="home-grid-item item13">
            <div className="home-grid-container info-card yellow ">
              <div className="contact-container">
                <div>
                  <h2 className="home-hero-title">Follow us</h2>
                  <div className="contact-sub-container">
                    {loading ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        <HomeSocialLink
                          href={data?.instagramLink}
                          platform="instagram"
                          label="Instagram"
                        />
                        <HomeSocialLink
                          href={data?.facebookLink}
                          platform="facebook"
                          label="Facebook"
                        />
                        <HomeSocialLink
                          href={data?.linkedinLink}
                          platform="linkedin"
                          label="LinkedIn"
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="phone-and-email-container">
                  {(data?.companyEmailAddress || data?.primaryPhoneNumber) && (
                    <div className="phone-email-contact-container">
                      {data?.companyEmailAddress && (
                        <a href={`mailto:${data.companyEmailAddress}`}>
                          {data.companyEmailAddress}
                        </a>
                      )}
                      {data?.primaryPhoneNumber && (
                        <p>{formatPhoneNumber(data.primaryPhoneNumber)}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
